/**
 * PonteDaSala — o que a janelinha de voz precisa saber da sala do LiveKit (Task 11, 24/10/2026).
 *
 * A sala de voz fica ESCONDIDA dentro da janelinha (`JanelaDaLigacao`): voz não tem palco, então a janelinha
 * desenha a própria cara. Só que o botão do microfone, o "Conectando…" e o "3 na ligação" dependem da sala,
 * e os hooks do LiveKit só enxergam a sala por DENTRO dela. Esta ponte é um filho do `InlineCallRoom` (o
 * `children` previsto para quem precisa de `useRoomContext()`) que lê a conexão, o microfone e os
 * participantes e devolve o que mudou por callback. Não desenha nada.
 *
 * São mudanças raras (não por tempo), então re-renderizar a janela com elas é seguro — os callbacks do
 * `InlineCallRoom` são estáveis por `ref` e a sala não se reconecta (ver o cabeçalho de `InlineCallRoom`).
 *
 * O erro de dispositivo vira texto CURTO FIXO ("Microfone bloqueado"), nunca a mensagem do navegador:
 * ela é inglesa e longa, não cabe numa janelinha de 52 px e `title` não existe no toque. A janela de VÍDEO
 * (`JanelaDeChamada`, A12) reaproveita a mesma ponte e escolhe o próprio texto fixo, que fala de câmera.
 */
import {
  useConnectionState,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
} from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";
import { useCallback, useEffect } from "react";

export interface EstadoDaSala {
  conectada: boolean;
  /** O LiveKit está religando sozinho (queda de rede): a ligação ainda não acabou. */
  reconectando: boolean;
  microfoneLigado: boolean;
  alternarMicrofone: () => void;
  /** Quantas pessoas estão na sala, contando eu. */
  participantes: number;
}

/** O texto do erro de dispositivo da ligação de VOZ — o padrão de quem não passa `textoDoErroDeDispositivo`. */
const ERRO_DE_DISPOSITIVO_DA_VOZ = "Microfone bloqueado";

export interface PonteDaSalaProps {
  /** `null` quando a sala sai do ar (a ligação acabou ou trocou). */
  onEstado: (estado: EstadoDaSala | null) => void;
  onErro: (mensagem: string) => void;
  /** O texto curto fixo que `onErro` recebe quando o navegador nega o microfone ou a câmera (nunca a mensagem do
   *  navegador). A voz não passa nada e fica com "Microfone bloqueado"; o vídeo fala de câmera. */
  textoDoErroDeDispositivo?: string;
}

export function PonteDaSala({
  onEstado,
  onErro,
  textoDoErroDeDispositivo = ERRO_DE_DISPOSITIVO_DA_VOZ,
}: PonteDaSalaProps) {
  const sala = useRoomContext();
  const conexao = useConnectionState();
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const quantos = useParticipants().length;

  const conectada = conexao === ConnectionState.Connected;
  const reconectando =
    conexao === ConnectionState.Reconnecting || conexao === ConnectionState.SignalReconnecting;

  const alternarMicrofone = useCallback(() => {
    // Negar a permissão rejeita aqui e também dispara o `MediaDevicesError` (tratado abaixo).
    void Promise.resolve(localParticipant?.setMicrophoneEnabled?.(!isMicrophoneEnabled)).catch(
      () => undefined
    );
  }, [localParticipant, isMicrophoneEnabled]);

  useEffect(() => {
    onEstado({
      conectada,
      reconectando,
      microfoneLigado: isMicrophoneEnabled,
      alternarMicrofone,
      participantes: quantos,
    });
  }, [onEstado, conectada, reconectando, isMicrophoneEnabled, alternarMicrofone, quantos]);

  // A sala saiu do ar: a janelinha não pode ficar com o estado dela.
  useEffect(() => () => onEstado(null), [onEstado]);

  useEffect(() => {
    const aoFalharODispositivo = () => onErro(textoDoErroDeDispositivo);
    sala.on(RoomEvent.MediaDevicesError, aoFalharODispositivo);
    return () => {
      sala.off(RoomEvent.MediaDevicesError, aoFalharODispositivo);
    };
  }, [sala, onErro, textoDoErroDeDispositivo]);

  return null;
}
